/*
 * YSW GA4 categories: storefront ecommerce events for creativeirishgifts.com
 * (ported from the Gael Song build, 2026-10-16)
 *
 * Pushes GA4 ecommerce objects to the dataLayer for:
 *   view_item         product page load (payload built in Liquid, ysw-ga4-datalayer.liquid)
 *   add_to_cart       any successful /cart/add call (product form, quick-add drawer,
 *                     product card and mini card quick add, featured product), plus
 *                     quantity increases through /cart/change or /cart/update
 *   remove_from_cart  quantity decreases and removals through /cart/change or /cart/update
 *   view_cart         cart page load, or the customer opening the cart drawer from the
 *                     header cart icon or a "View cart" link (cfg.cartOpenSelector);
 *                     not the automatic open after an add
 *
 * Every push carries ysw_source: 'ga4cat'. The GTM trigger requires it, so gtag
 * commands from other scripts on the page never fire our tag.
 *
 * Categories come from the hidden line item properties (_category, _subcategory,
 * _product_type, _strategic) that ysw-ga4-properties.liquid adds to each form, and
 * which Shopify returns in every cart response.
 *
 * item_id: cfg.itemId = 'product' sends the Shopify product id (what the store's
 * GA4 and Ads remarketing used before), 'shopify' sends shopify_<CC>_<product>_<variant>.
 * Cart lines matching cfg.exclude (regex on title, product type, vendor; fee and
 * surcharge products added by apps) are left out.
 *
 * Debug: localStorage.setItem('ysw_ga4cat_debug', '1') logs each push to the console.
 */
(function () {
  'use strict';

  var cfg = window.YSW_GA4CAT;
  if (!cfg || window.__yswGa4CatLoaded) return;
  window.__yswGa4CatLoaded = true;

  var dataLayer = (window.dataLayer = window.dataLayer || []);
  var country = cfg.country || 'ZZ';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_CALL = /\/cart\/(add|change|update)(?:\.js)?(?:[?#]|$)/;
  var exclude = null;
  try { if (cfg.exclude) exclude = new RegExp(cfg.exclude, 'i'); } catch (e) {}
  var debug = false;
  try { debug = window.localStorage.getItem('ysw_ga4cat_debug') === '1'; } catch (e) {}

  var nativeFetch = window.fetch;
  var snapshot = byKey(cfg.cart);

  function noop() {}

  function log() {
    if (debug && window.console) console.log.apply(console, ['[ysw-ga4cat]'].concat([].slice.call(arguments)));
  }

  function push(event, ecommerce) {
    if (!ecommerce || !ecommerce.items || !ecommerce.items.length) return;
    dataLayer.push({ ecommerce: null });
    dataLayer.push({ event: event, ysw_source: 'ga4cat', ecommerce: ecommerce });
    // GTM's data model keeps ysw_source after this push, so a later gtag event
    // from another script would match our trigger and re-send this ecommerce.
    // Clear it once our event is processed.
    dataLayer.push({ ysw_source: null });
    log(event, ecommerce);
  }

  function compact(obj) {
    var out = {};
    Object.keys(obj).forEach(function (k) {
      if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') out[k] = obj[k];
    });
    return out;
  }

  function money(cents) {
    return Math.round(Number(cents) || 0) / 100;
  }

  function itemId(productId, variantId) {
    if (cfg.itemId === 'shopify') return 'shopify_' + country + '_' + productId + '_' + variantId;
    return String(productId);
  }

  function excluded(line) {
    if (!exclude || !line) return false;
    return exclude.test([line.product_title, line.title, line.product_type, line.vendor].join(' | '));
  }

  function toItem(line, quantity, index) {
    var p = line.properties || {};
    return compact({
      item_id: itemId(line.product_id, line.variant_id),
      item_name: line.product_title,
      item_brand: line.vendor,
      item_variant: line.variant_title,
      item_category: p._category,
      item_category2: p._subcategory,
      item_category3: p._product_type,
      strategic: p._strategic,
      sku: line.sku,
      price: money(line.final_price != null ? line.final_price : line.price),
      quantity: quantity,
      index: index
    });
  }

  function ecommerceFor(items) {
    var value = items.reduce(function (sum, it) { return sum + (it.price || 0) * (it.quantity || 0); }, 0);
    return { currency: cfg.currency, value: Math.round(value * 100) / 100, items: items };
  }

  function byKey(cart) {
    var map = {};
    ((cart && cart.items) || []).forEach(function (line) {
      if (!excluded(line)) map[line.key] = line;
    });
    return map;
  }

  function refreshSnapshot() {
    return nativeFetch.call(window, root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { snapshot = byKey(cart); cfg.cart = cart; return cart; })
      .catch(noop);
  }

  // Quantities the customer asked to add, by variant id, read from the request body.
  function requestedQuantities(body) {
    var out = {};
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var id = body.get('id');
        if (id) out[id] = Number(body.get('quantity')) || 1;
      } else if (typeof body === 'string' && body.charAt(0) === '{') {
        var json = JSON.parse(body);
        (json.items || [json]).forEach(function (it) { if (it.id) out[it.id] = Number(it.quantity) || 1; });
      } else if (typeof body === 'string' || (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)) {
        var params = new URLSearchParams(body);
        if (params.get('id')) out[params.get('id')] = Number(params.get('quantity')) || 1;
      }
    } catch (e) {}
    return out;
  }

  function onAdd(body, data) {
    var lines = data && data.items ? data.items : data && data.variant_id ? [data] : [];
    var asked = requestedQuantities(body);
    var items = lines.filter(function (line) { return !excluded(line); }).map(function (line, i) {
      var qty = asked[line.variant_id] || asked[line.id] || line.quantity || 1;
      return toItem(line, qty, i);
    });
    push('add_to_cart', ecommerceFor(items));
    refreshSnapshot();
  }

  function onChange(data) {
    if (!data || !data.items) { refreshSnapshot(); return; }
    var next = byKey(data);
    var removed = [];
    var added = [];
    Object.keys(snapshot).forEach(function (key) {
      var before = snapshot[key].quantity;
      var after = next[key] ? next[key].quantity : 0;
      if (after < before) removed.push(toItem(snapshot[key], before - after, removed.length));
    });
    Object.keys(next).forEach(function (key) {
      var before = snapshot[key] ? snapshot[key].quantity : 0;
      var after = next[key].quantity;
      if (after > before) added.push(toItem(next[key], after - before, added.length));
    });
    snapshot = next;
    cfg.cart = data;
    push('remove_from_cart', ecommerceFor(removed));
    push('add_to_cart', ecommerceFor(added));
  }

  function viewCart() {
    refreshSnapshot().then(function () {
      var items = Object.keys(snapshot).map(function (key, i) { return toItem(snapshot[key], snapshot[key].quantity, i); });
      push('view_cart', ecommerceFor(items));
    });
  }

  // Wrap fetch. Shopify's own AddToCartListener already wraps it for web pixels;
  // this chains on top and returns the untouched response to the theme.
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var match = url.match(CART_CALL);
      var result = nativeFetch.apply(this, arguments);
      if (!match) return result;
      var body = init && init.body;
      return result.then(function (response) {
        try {
          if (response && response.ok) {
            response.clone().json().then(function (data) {
              if (match[1] === 'add') onAdd(body, data);
              else onChange(data);
            }).catch(noop);
          }
        } catch (e) {}
        return response;
      });
    };
  }

  // view_item
  if (cfg.viewItem) {
    cfg.viewItem.items = (cfg.viewItem.items || []).map(compact);
    push('view_item', cfg.viewItem);
  }

  // view_cart: cart page, or a customer-initiated drawer open. With the cart type
  // set to "page" there is no <cart-drawer>; the icon is a plain link to /cart and
  // the cart page load sends view_cart.
  if (cfg.template === 'cart') viewCart();
  var openSelector = cfg.cartOpenSelector || '[data-cart-link]';
  document.addEventListener('click', function (e) {
    if (cfg.template === 'cart') return;
    if (cfg.drawerSelector && document.querySelector && !document.querySelector(cfg.drawerSelector)) return;
    if (e.target && e.target.closest && e.target.closest(openSelector)) viewCart();
  }, true);
})();
